import React, { useState, useRef } from 'react';
import { createUserWithEmailAndPassword, sendEmailVerification, deleteUser } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../firebase';
import { useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import '../styles/Signup.css';

function Signup() {
  const navigate = useNavigate();
  const recaptchaRef = useRef();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    companyName: '',
    industry: '',
    region: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [recaptchaToken, setRecaptchaToken] = useState(null);

  const industries = [
    'Construction',
    'Manufacturing',
    'Transportation & Logistics',
    'Healthcare',
    'Mining',
    'Energy & Utilities',
    'Agriculture',
    'Hospitality',
    'Retail',
    'Warehousing',
    'Emergency Services',
    'Aviation',
    'Maritime',
    'Other'
  ];

  const countries = [
    'United States',
    'United Kingdom',
    'Canada',
    'Australia',
    'Germany',
    'France',
    'Spain',
    'Italy',
    'Netherlands',
    'Belgium',
    'Switzerland',
    'Austria',
    'Sweden',
    'Norway',
    'Denmark',
    'Finland',
    'Poland',
    'Ireland',
    'Portugal',
    'Greece',
    'Czech Republic',
    'Hungary',
    'Romania',
    'Bulgaria',
    'Croatia',
    'Slovakia',
    'Slovenia',
    'Luxembourg',
    'Estonia',
    'Latvia',
    'Lithuania',
    'Cyprus',
    'Malta',
    'Iceland',
    'Japan',
    'South Korea',
    'China',
    'India',
    'Singapore',
    'Hong Kong',
    'Taiwan',
    'Thailand',
    'Malaysia',
    'Indonesia',
    'Philippines',
    'Vietnam',
    'New Zealand',
    'Mexico',
    'Brazil',
    'Argentina',
    'Chile',
    'Colombia',
    'Peru',
    'South Africa',
    'United Arab Emirates',
    'Saudi Arabia',
    'Israel',
    'Turkey',
    'Russia',
    'Ukraine',
    'Other'
  ].sort();

  const generateCompanyCode = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return code;
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  const sanitizeCompanyName = (name) => {
    return name.trim().slice(0, 100).replace(/[<>]/g, '');
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const onRecaptchaChange = (token) => {
    setRecaptchaToken(token);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!recaptchaToken) {
      setError('Please complete the reCAPTCHA verification');
      return;
    }

    // Validation
    if (!validateEmail(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    const hasUpperCase = /[A-Z]/.test(formData.password);
    const hasLowerCase = /[a-z]/.test(formData.password);
    const hasNumbers = /\d/.test(formData.password);
    
    if (!hasUpperCase || !hasLowerCase || !hasNumbers) {
      setError('Password must contain uppercase, lowercase, and numbers');
      return;
    }

    const sanitizedCompanyName = sanitizeCompanyName(formData.companyName);
    if (!sanitizedCompanyName || sanitizedCompanyName.length < 2) {
      setError('Company name must be at least 2 characters');
      return;
    }

    if (!formData.industry) {
      setError('Please select an industry');
      return;
    }

    if (!formData.region) {
      setError('Please select a region');
      return;
    }

    setLoading(true);

    let createdUser = null;

    try {
      // Step 1: Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        formData.email,
        formData.password
      );
      createdUser = userCredential.user;

      // Step 2: Send verification email immediately before App Check kicks in
      let verificationSent = false;
      try {
        await sendEmailVerification(createdUser);
        verificationSent = true;
      } catch (verificationError) {
        console.error('Verification email failed:', verificationError);
      }

      // Step 3: Generate company code
      const companyCode = generateCompanyCode();

      // Step 4: Call Cloud Function to create company and admin (SERVER-SIDE)
      const functions = getFunctions();
      const createAdmin = httpsCallable(functions, 'createAdminAccount');
      
      await createAdmin({
        companyName: sanitizedCompanyName,
        industry: formData.industry,
        region: formData.region,
        companyCode: companyCode,
        recaptchaToken: recaptchaToken
      });

      // Success
      if (verificationSent) {
        alert(
          `Admin account created successfully!\n\n` +
          `Company Code: ${companyCode}\n\n` +
          `IMPORTANT:\n` +
          `1. Check your email (${formData.email}) to verify your account\n` +
          `2. You must verify your email before accessing the dashboard\n` +
          `3. Share your company code with employees to join\n\n` +
          `Note: This is an ADMIN account for the web dashboard only.\n` +
          `Employees should download the mobile app and use the company code to join.\n\n` +
          `Keep your company code secure!`
        );
      } else {
        alert(
          `Admin account created successfully!\n\n` +
          `Company Code: ${companyCode}\n\n` +
          `We couldn't send the verification email.\n` +
          `You can request a new one after logging in.`
        );
      }

      navigate('/login');

    } catch (err) {
      console.error('Signup error:', err);

      // Reset reCAPTCHA on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
        setRecaptchaToken(null);
      }

      // Rollback Firebase Auth user on failure
      if (createdUser) {
        try {
          await deleteUser(createdUser);
        } catch (deleteError) {
          console.error('Failed to delete user:', deleteError);
        }
      }

      // User-friendly error messages
      if (err.code === 'auth/email-already-in-use') {
        setError('This email is already registered');
      } else if (err.code === 'auth/invalid-email') {
        setError('Invalid email address');
      } else if (err.code === 'auth/weak-password') {
        setError('Password is too weak');
      } else if (err.code === 'auth/network-request-failed') {
        setError('Network error. Please check your connection.');
      } else if (err.message?.includes('already-exists')) {
        setError('An admin account already exists for this email');
      } else if (err.message?.includes('permission')) {
        setError('Setup failed. Please try again or contact support.');
      } else if (err.message?.includes('recaptcha')) {
        setError('reCAPTCHA verification failed. Please try again.');
      } else {
        setError('Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-page">
      <div className="signup-container">
        <div className="signup-header">
          <h1>Zella</h1>
          <h2>Create Admin Account</h2>
          <p>Set up your company dashboard</p>
        </div>
        
        {error && <div className="error-message">{error}</div>}
        
        <form className="signup-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="companyName">Company Name</label>
            <input
              type="text"
              id="companyName"
              name="companyName"
              value={formData.companyName}
              onChange={handleChange}
              placeholder="Acme Corporation..."
              maxLength="100"
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="industry">Industry</label>
            <select
              id="industry"
              name="industry"
              value={formData.industry}
              onChange={handleChange}
              required
            >
              <option value="">Select your industry...</option>
              {industries.map(industry => (
                <option key={industry} value={industry}>{industry}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="region">Region / Country</label>
            <select
              id="region"
              name="region"
              value={formData.region}
              onChange={handleChange}
              required
            >
              <option value="">Select your region...</option>
              {countries.map(country => (
                <option key={country} value={country}>{country}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="email">Admin Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="admin@company.com..."
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Min. 8 characters..."
                minLength="8"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            </div>
            <small>Must contain uppercase, lowercase, and numbers</small>
          </div>

          <div className="form-group">
            <label htmlFor="confirmPassword">Confirm Password</label>
            <div className="password-input-wrapper">
              <input
                type={showConfirmPassword ? "text" : "password"}
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Re-enter password..."
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                aria-label={showConfirmPassword ? "Hide password" : "Show password"}
              >
                {showConfirmPassword ? (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
                    <line x1="1" y1="1" x2="23" y2="23"></line>
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                    <circle cx="12" cy="12" r="3"></circle>
                  </svg>
                )}
              </button>
            </div>
          </div>

          <div className="form-group recaptcha-group">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={process.env.REACT_APP_RECAPTCHA_V2_SITE_KEY}
              onChange={onRecaptchaChange}
            />
          </div>
          
          <button type="submit" className="btn-signup-submit" disabled={loading || !recaptchaToken}>
            {loading ? 'Creating Account...' : 'Create Account'}
          </button>
          
          <div className="signup-footer">
            <p>Already have an account? <a href="/login">Log In</a></p>
          </div>
        </form>
        
        <div className="back-to-home">
          <a href="/">← Back to home</a>
        </div>
      </div>
    </div>
  );
}

export default Signup;